import { useState, useEffect } from "react";
import { useAuth } from "../hooks/useAuth";
import { useNavigate, Link } from "react-router-dom";
import { isTauri } from "../lib/tauri";
import { Icon } from "../components/icons";

export function Login() {
  const { user, loading: authLoading, login, setNewPassword, needsNewPassword, error } = useAuth();
  const navigate = useNavigate();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [newPassword, setNewPasswordVal] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);

  useEffect(() => {
    if (!authLoading && user) {
      navigate("/generate", { replace: true });
    }
  }, [authLoading, user, navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setLocalError(null);
    try {
      const result = await login(email, password);
      if (!result.needsNewPassword) navigate("/generate");
    } catch {
      // error surfaced via useAuth
    } finally {
      setLoading(false);
    }
  };

  const handleNewPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setLocalError(null);
    if (newPassword !== confirmPassword) {
      setLocalError("Passwords don't match");
      return;
    }
    if (newPassword.length < 8) {
      setLocalError("Password must be at least 8 characters");
      return;
    }
    setLoading(true);
    try {
      await setNewPassword(newPassword);
      navigate("/generate");
    } catch {
      // error surfaced via useAuth
    } finally {
      setLoading(false);
    }
  };

  if (authLoading) {
    return (
      <div className="auth-shell">
        <div className="spin" />
      </div>
    );
  }

  const displayError = localError || error;

  return (
    <div className="auth-shell">
      <div className="auth-card card">
        <div className="auth-head">
          <img src="/logo.png" alt="Chatter" className="auth-logo" />
          <h1 className="ph-title" style={{ fontSize: 26 }}>Chatter</h1>
          <p className="ph-lede" style={{ marginTop: 4 }}>AI voice generation</p>
        </div>

        {displayError && (
          <div className="auth-error">
            <span>{displayError}</span>
          </div>
        )}

        {needsNewPassword ? (
          <form onSubmit={handleNewPassword} className="auth-form">
            <div className="ta-hint" style={{ marginTop: 0 }}>
              Please set a new password to continue.
            </div>
            <label className="auth-field">
              <span className="auth-label">New password</span>
              <input
                type="password"
                value={newPassword}
                onChange={(e) => setNewPasswordVal(e.target.value)}
                className="auth-input"
                placeholder="••••••••"
                required
                minLength={8}
                autoComplete="new-password"
              />
            </label>
            <label className="auth-field">
              <span className="auth-label">Confirm password</span>
              <input
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="auth-input"
                placeholder="••••••••"
                required
                minLength={8}
                autoComplete="new-password"
              />
            </label>
            <button
              type="submit"
              disabled={loading}
              className={`btn btn-primary auth-submit${loading ? " disabled" : ""}`}
            >
              {loading ? <span className="spin" /> : "Set password"}
            </button>
          </form>
        ) : (
          <form onSubmit={handleSubmit} className="auth-form">
            <label className="auth-field">
              <span className="auth-label">Email</span>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="auth-input"
                placeholder="you@example.com"
                required
                autoComplete="email"
              />
            </label>
            <label className="auth-field">
              <span className="auth-label">Password</span>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="auth-input"
                placeholder="••••••••"
                required
                minLength={8}
                autoComplete="current-password"
              />
            </label>
            <button
              type="submit"
              disabled={loading}
              className={`btn btn-primary auth-submit${loading ? " disabled" : ""}`}
            >
              {loading ? <span className="spin" /> : "Sign in"}
            </button>
          </form>
        )}

        {!isTauri() && (
          <div className="auth-foot">
            <Link to="/download" className="auth-link">
              <Icon.Download /> Get the desktop app
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}
